import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "./constants";

/**
 * Centralises the four batch mutations on /customers. Each helper sets
 * `busyAction` to a string (used by the toolbar to disable other buttons
 * and show a spinner) and is responsible for its own toast/error handling.
 *
 * Callers pass:
 *   - selectedArray (all selected ids, including drafts)
 *   - finalizedSelectedArray (drafts filtered out — screening/PDF/CSV)
 *   - onSuccess (refresh the table + clear selection)
 *
 * The hook returns `{ busyAction, batchProgress, runScreening, downloadPdfs,
 * exportCsv, deleteBatch }`.
 */
export function useBatchActions({ selectedArray, finalizedSelectedArray, onSuccess }) {
  const [busyAction, setBusyAction] = useState(null);
  const [batchProgress, setBatchProgress] = useState(null);

  const runScreening = async () => {
    const ids = finalizedSelectedArray;
    if (ids.length === 0) return;
    setBusyAction("screening");
    setBatchProgress({
      label: `Verificando ${ids.length} cliente${ids.length !== 1 ? "s" : ""}…`,
    });
    try {
      const res = await axios.post(
        `${API}/customers/batch/screening`,
        { customer_ids: ids },
        { withCredentials: true },
      );
      const { clean, matches, failed } = res.data;
      toast.success(
        `Screening completado: ${clean} sin coincidencias, ${matches} con coincidencias${failed ? `, ${failed} con error` : ""}`,
        { duration: 8000 },
      );
      onSuccess?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "monthly_limit_exceeded") {
        toast.error(
          `No tienes suficientes screenings disponibles. Necesitas ${d.needed}, te quedan ${d.available}.`,
          { duration: 10000 },
        );
      } else {
        toast.error(d || err.message || "Error en el screening");
      }
    } finally {
      setBusyAction(null);
      setBatchProgress(null);
    }
  };

  const downloadPdfs = async () => {
    const ids = finalizedSelectedArray;
    if (ids.length === 0) return;
    setBusyAction("pdfs");
    setBatchProgress({ label: `Generando ${ids.length} PDF${ids.length !== 1 ? "s" : ""}…` });
    try {
      const res = await axios.post(
        `${API}/customers/batch/generate-pdfs`,
        { customer_ids: ids },
        { withCredentials: true, responseType: "blob" },
      );
      const generated = Number(res.headers["x-batch-generated"] || 0);
      const skipped = Number(res.headers["x-batch-skipped"] || 0);
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `kyc_pdfs_${Date.now()}.zip`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(
        skipped > 0
          ? `Generados ${generated} PDFs, ${skipped} omitidos (KYC no verificado)`
          : `Generados ${generated} PDFs`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Error al generar PDFs");
    } finally {
      setBusyAction(null);
      setBatchProgress(null);
    }
  };

  const exportCsv = async () => {
    const ids = finalizedSelectedArray;
    if (ids.length === 0) return;
    setBusyAction("export");
    try {
      const res = await axios.post(
        `${API}/customers/batch/export`,
        { customer_ids: ids },
        { withCredentials: true, responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "text/csv; charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `clientes_${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Exportados ${ids.length} clientes a CSV`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Error al exportar");
    } finally {
      setBusyAction(null);
    }
  };

  const deleteBatch = async () => {
    const n = selectedArray.length;
    if (n === 0) return;
    if (!window.confirm(`Estás a punto de eliminar ${n} cliente${n !== 1 ? "s" : ""}. Esta acción no se puede deshacer.`)) {
      return;
    }
    if (!window.confirm(`Confirma una vez más: eliminar ${n} cliente${n !== 1 ? "s" : ""}.`)) {
      return;
    }
    setBusyAction("delete");
    try {
      const res = await axios.post(
        `${API}/customers/batch/delete`,
        { customer_ids: selectedArray },
        { withCredentials: true },
      );
      toast.success(`Eliminados ${res.data.deleted} de ${res.data.requested}`);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Error al eliminar");
    } finally {
      setBusyAction(null);
    }
  };

  return { busyAction, batchProgress, runScreening, downloadPdfs, exportCsv, deleteBatch };
}
